import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Label,
  FormGroup,
  Input
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import Loader from "../../../components/Loader/Loader";
import { failed, success } from "../../../utils/Tostify";
import TextInput from "../../../components/InputFields/TextInput";
import Uploadform from "../../../assets/images/upload_form.svg";
import { formikAddApplicationDoc } from "../Constant";
import { handleFileUpload } from "../../../utils/ImageToBase64";
import { addApplicationDoc, getApplicationById } from "../../../api/Applications";
import DatePickerSelector from "../../../components/InputFields/DatePicker";
import moment from "moment";
import { decryptData } from "../../../utils/helper";
import { getDocumentTypeList } from '../../../api/workflow';
import { useDropzone } from "react-dropzone";
import * as Yup from "yup";

const AddApplicationDocumentModal = ({
  refetch,
  setShowModal,
  applicationId,
  patientDocuments,
  patientApplication,
  status,
  taskId
}) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userReducer.token);
  const [isLoading, setIsLoading] = useState(false);
  const [documentType, setDocumentType] = useState("new"); // "existing" or "new"
  const [selectedPatientDocument, setSelectedPatientDocument] = useState("");
  const [isExpirationDateMandatory, setIsExpirationDateMandatory] = useState(false);
  const [errorShow, setErrorShow] = useState("");
  const [documentsOptions,setDocumentsOptions] = useState([]);
  const [application, setApplication] = useState();
  const getDocumentList = async (page, limit, searchKey = "") => {
    let res;
    try {
        setIsLoading(true);
        res = await getDocumentTypeList({ dispatch, token, page, limit, searchKey });
        const responseData = decryptData(res?.data);
        // Ensure responseData contains 'items' and it's an array
        if (res?.status === "SUCCESS" && Array.isArray(responseData.items)) {
            const options = responseData.items.map((doc) => ({
                label: doc.documentTitle,
                value: doc._id
            }));
            setDocumentsOptions(options);
        } else {
            failed("Invalid response format or empty list");
        }
    } catch (error) {
        failed(res?.message || "Something went wrong");
    } finally {
        setIsLoading(false);
    }
};

const getApplication = async () => {
  let res;
  try {
    setIsLoading(true);
    res = await getApplicationById({ dispatch, token, applicationId: applicationId});
    const responseData = decryptData(res?.data);
    if (res?.status === "SUCCESS") {
      setApplication(responseData);
      setIsLoading(false);
    }
  } catch (error) {
    failed(res?.message);
    setIsLoading(false);
  }
};

  useEffect(()=>{
    getDocumentList();
    getApplication();
  },[]);

  const validationSchema = Yup.object().shape(
    documentType === "new"
      ? {
          docTitle: Yup.string().required("Document Title is required"),
          documentTypeId: Yup.string().required("Document Type is required")
        }
      : {
          documentId: Yup.string().required("Please select an existing document")
        }
  );

  const formik = useFormik({
    ...formikAddApplicationDoc,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      let payload;

      if (documentType === "existing" && selectedPatientDocument) {
        payload = {
          documentType,
          documentId: selectedPatientDocument._id,
          documentTypeId: selectedPatientDocument.documentTypeId || "",
          uploadedBy: selectedPatientDocument?.uploadedBy,
          title: selectedPatientDocument?.title,
          filePath: selectedPatientDocument?.filePath,
          status,
          taskId
        };
      } else {
        let base64Files = [];

        for (let file of selectedFiles) {
          let base64pdfForm = await handleFileUpload(file);
          let fileExtension = file.type.split("/")[1];

          base64Files.push({
            base64String: base64pdfForm,
            extension: fileExtension,
            name: file.name
          });
        }

        payload = {
          ...values,
          docTitle: values.docTitle,
          patientId: application?.patientData?._id,
          docFile: base64Files[0], // If supporting only 1 file upload. Otherwise, use `docFiles: base64Files`
          documentType,
          documentTypeId: values.documentTypeId || "",
          expirationDate: isExpirationDateMandatory ? values.expirationDate : "",
          status,
          taskId
          // uploadedBy: user._id
        };
      }
      try {
        setIsLoading(true);
        const res = await addApplicationDoc({
          values: payload,
          dispatch,
          token,
          applicationId
        });

        if (res?.status === "SUCCESS") {
          // await refetch();
          if (typeof refetch === "function") {
            await refetch();
          }
          success(res.message);
          setShowModal(false);
        } else {
          failed(res?.message);
        }
      } catch (error) {
        // console.log('error----',error);
        failed(error.message || "Something went wrong");
      } finally {
        setIsLoading(false);
      }
    }
  });

  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleRemoveFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setSelectedFiles([acceptedFiles[0]]); // Keep only the first file
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/jpeg,image/png,application/pdf",
    multiple: false // ✅ Only allow one file
  });

  const docTypeFrequency = patientApplication?.applicationDocuments?.reduce((acc, doc) => {
    const id = doc.documentTypeId;
    acc[id] = (acc[id] || 0) + 1;
    return acc;
  }, {});

  // Step 2: Filter patientDocuments
  const filteredPatientDocuments = patientDocuments?.filter(
    (doc) => (docTypeFrequency?.[doc.documentTypeId] || 0) < 2
  );

  return (
    <Modal isOpen={true} toggle={() => setShowModal(false)} size="lg">
      <ModalHeader toggle={() => setShowModal(false)} className="assign_blk p-0 mb-3">
        Add Document
      </ModalHeader>
      <ModalBody className="form-add add_form">
        {isLoading && <Loader />}
        <Row className="m-0">
          <Col lg={12} className="mb-3">
            <Label>Choose Document Type</Label>
            <FormGroup check>
              <Label check>
                <Input
                  type="radio"
                  name="documentType"
                  value="existing"
                  checked={documentType === "existing"}
                  onChange={() => setDocumentType("existing")}
                />{" "}
                Existing Document
              </Label>
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="radio"
                  name="documentType"
                  value="new"
                  checked={documentType === "new"}
                  onChange={() => setDocumentType("new")}
                />{" "}
                New Document
              </Label>
            </FormGroup>
          </Col>

          {documentType === "existing" ? (
            <Col lg={12} className="mb-3">
              <Label>Select Patient Document</Label>
              <Input
                type="select"
                value={selectedPatientDocument?._id || formik.values.documentId}
                onChange={(e) => {
                  const doc = patientDocuments.find((doc) => doc._id === e.target.value) || "";
                  setSelectedPatientDocument(doc);
                  formik.setFieldValue("documentId", doc?._id || "");
                }}>
                <option value="">Select Document</option>
                {filteredPatientDocuments?.map((doc) => (
                  <option key={doc?._id} value={doc?._id}>
                    {doc?.title}
                  </option>
                ))}
              </Input>
              {formik.errors.documentId && formik.touched.documentId && (
                <div className="text-danger">{formik.errors.documentId}</div>
              )}
            </Col>
          ) : (
            <>
              <Row className="mb-2 mx-0">
                <Col lg={6} className="location_block">
                <TextInput
                  keyField={"documentTypeId"}
                  label="Document Type"
                  formik={formik}
                  options={documentsOptions}
                  type="select"
                  placeholder="Select"
                  value={formik.values.documentTypeId}
                  onChange={(e) =>
                    formik.setFieldValue("documentTypeId", e.target.value)
                  }
                />
                </Col>
                <Col lg={6} className="location_block">
                <TextInput
                    keyField={"docTitle"}
                    formik={formik}
                    label="Document Title"
                    type="text"
                    placeholder="Document Title"
                    value={formik.values.docTitle}
                    onChange={(e) => formik.setFieldValue("docTitle", e.target.value)}
                />
                </Col>
                <Col lg={6} className="location_block">
                  <Label check>
                    <input
                      type="checkbox"
                      checked={isExpirationDateMandatory}
                      onChange={(e) => {
                        setIsExpirationDateMandatory(e.target.checked);
                        setErrorShow("");
                        if (!e.target.checked) {
                          formik.setFieldValue("expirationDate", "");
                        }
                      }}
                    />
                    {" "}Is Expiration Date Mandatory?
                  </Label>
                </Col>
                {isExpirationDateMandatory && (
                  <Col lg={6} className="location_block">
                    <DatePickerSelector
                      keyField="expirationDate"
                      formik={formik}
                      value={formik.values.expirationDate}
                      minDate={moment()}
                      label="Expiration Date"
                      required
                      onChange={(e) => {
                        formik.setFieldValue("expirationDate", moment(e).format("MM-DD-YYYY"));
                        setErrorShow("");
                      }}
                    />
                    <span style={{ color: "red" }}>{errorShow}</span>
                  </Col>
                )}
              </Row>
                <Row className="m-0">
                  <Label for="exampleFile">Attachment</Label>
                  <div {...getRootProps()} className="upload-area text-center">
                    <input {...getInputProps()} />
                    <img src={Uploadform} alt="upload" />
                    <p>Drag & drop files here, or click to browse</p>
                    <button type="button" className="browse_blk">Browse</button>
                  </div>
                </Row>

                <Row className="mt-3 mx-0" style={{ paddingTop: "20px" }}>
                  <Col md={6}>
                    {selectedFiles.length > 0 && (
                      <div className="uploaded-files mt-3">
                        {selectedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="file-item d-flex justify-content-between align-items-center"
                          >
                            <p style={{ zIndex: "99" }}>{file.name}</p>
                            <button type="button" className="cancel-btn" onClick={() => handleRemoveFile(index)}>
                              <i className="fas fa-times" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </Col>
                </Row>

            </>
          )}
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setShowModal(false)} className="save_btn cancel_btn">
          Cancel
        </Button>
        <Button color="primary" onClick={formik.handleSubmit} className="save_btn">
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddApplicationDocumentModal;

